import type { ResearchVerification } from "./research-verification-schema";
import { researchQuotationMatches } from "./research-quotation";
import { matchesSourcedPerson } from "./research-sourced-person";
import { researchReasonedMatch } from "./research-reasoned-match";
import {
  researchPhoneMatches,
  researchQuotationHasPhone,
} from "./research-phone-identity";
export * from "./research-verification-schema";

export * from "./research-identity-contract";
import {
  RESEARCH_IDENTITY_POLICY_VERSION,
  invalidResearchCompanyName,
  type ResearchOriginalIdentity,
  type ResearchObservation,
  type ResearchAcceptance,
} from "./research-identity-contract";

export function normalizedResearchText(value: string): string {
  return value.normalize("NFKC").replace(/\s+/g, " ").trim().toLowerCase();
}

function identifierText(value: string, kind: string): string {
  return kind === "phone"
    ? value.replace(/\D/g, "")
    : normalizedResearchText(value);
}

/** Keep observed support, then trust the assessor's best-match judgment. */
export function decideResearchAcceptance(input: {
  fieldKey: string;
  value: unknown;
  subject: "person" | "business" | "link";
  original: ResearchOriginalIdentity;
  observations: ResearchObservation[];
  verification?: ResearchVerification | null;
}): ResearchAcceptance {
  const nameField = ["company", "displayName"].includes(input.fieldKey);
  const invalid = nameField ? invalidResearchCompanyName(input.value) : null;
  if (invalid) return { decision: "reject", reason: invalid };
  if (
    ["companyDescription", "description"].includes(input.fieldKey) &&
    typeof input.value === "string" &&
    /\b(?:(?:I|we) (?:could not|couldn't|cannot|can't|was unable to|were unable to) (?:verify|confirm|find|identify)|(?:available (?:public )?(?:sources|information)|public information) (?:strongly links|do|does|cannot|could not)|(?:identity|profile) (?:cannot|could not) be (?:confirmed|verified))\b/i.test(
      input.value,
    )
  )
    return { decision: "reject", reason: "RESEARCH_NOTES_NOT_FACT" };
  if (
    input.subject === "business" &&
    input.original.businessName &&
    invalidResearchCompanyName(input.original.businessName)
  )
    return {
      decision: "candidate",
      reason: "BUSINESS_IDENTIFIER_NOT_CORROBORATED",
    };
  const check = input.verification;
  if (!check) return { decision: "candidate", reason: "SOURCE_NOT_VERIFIED" };
  if (
    (nameField && check.nameValidity === "invalid") ||
    (input.subject !== "business" && check.personMatch === "different") ||
    (input.subject === "business" && check.businessMatch === "different") ||
    (input.subject === "link" && check.dealBusinessMatch === "different")
  ) {
    return { decision: "reject", reason: check.reason };
  }
  const quotes = check.quotes.filter((quote) => {
    const page = input.observations.find(
      (item) => item.id === quote.observationId,
    );
    return page && researchQuotationMatches(page.text, quote.text);
  });
  const reasoned = researchReasonedMatch({ ...input, verification: check });
  const quoted = quotes.length > 0 && quotes.length === check.quotes.length;
  if (
    !quoted ||
    !check.factSupported ||
    !quotes.some((q) => q.purpose === "fact")
  ) {
    return { decision: "candidate", reason: "FACT_QUOTATION_REQUIRED" };
  }
  if (
    nameField &&
    (check.nameValidity !== "valid" ||
      (!reasoned && !quotes.some((q) => q.purpose === "name")))
  ) {
    return { decision: "candidate", reason: "TRADING_NAME_NOT_ESTABLISHED" };
  }
  // A person's ownership is separate from their management titles. Identity and
  // title may have separate quotations on the same observed profile page.
  if (
    input.fieldKey === "role" &&
    !reasoned &&
    typeof input.value === "string" &&
    /\b(?:owners?|founders?|proprietors?)\b/i.test(input.value) &&
    !quotes.some(
      (quote) =>
        quote.purpose === "fact" &&
        /\b(?:owners?|owns|founders?|proprietors?)\b/i.test(quote.text) &&
        input.original.name &&
        (normalizedResearchText(quote.text).includes(
          normalizedResearchText(input.original.name),
        ) ||
          quotes.some(
            (identity) =>
              identity.purpose === "identity" &&
              identity.observationId === quote.observationId &&
              normalizedResearchText(identity.text).includes(
                normalizedResearchText(input.original.name ?? ""),
              ),
          )),
    )
  )
    return { decision: "candidate", reason: "FACT_QUOTATION_REQUIRED" };
  const anchored = check.identifiers.some((identifier) => {
    const expected = input.original[identifier.kind];
    if (
      !expected ||
      !(identifier.kind === "phone"
        ? researchPhoneMatches(expected, identifier.value)
        : identifierText(expected, identifier.kind) ===
          identifierText(identifier.value, identifier.kind))
    )
      return false;
    // City alone and an arbitrary phone fragment are not identity identifiers.
    if (identifier.kind === "address" && !/\d/.test(expected)) return false;
    if (
      identifier.kind === "phone" &&
      identifierText(expected, "phone").length < 8
    )
      return false;
    return quotes.some(
      (quote) =>
        (quote.purpose === "identity" ||
          (input.subject === "business" &&
            identifier.kind === "businessName" &&
            quote.purpose === "name")) &&
        quote.observationId === identifier.observationId &&
        (identifier.kind === "phone"
          ? researchQuotationHasPhone(quote.text, expected)
          : identifierText(quote.text, identifier.kind).includes(
              identifierText(expected, identifier.kind),
            )) &&
        (identifier.kind !== "businessName" ||
          (input.original.name &&
            normalizedResearchText(quote.text).includes(
              normalizedResearchText(input.original.name),
            ))),
    );
  });
  // A reasoned decision can use one source and converging clues. Retain the
  // older contextual route for assessments without a decision confidence.
  const contextual =
    reasoned ||
    (check.identityBasis === "corroborated_context" &&
      new Set(
        quotes
          .filter((q) => q.purpose === "identity")
          .flatMap((q) => {
            const observation = input.observations.find(
              (o) => o.id === q.observationId,
            );
            try {
              return observation
                ? [new URL(observation.url).hostname.replace(/^www\./, "")]
                : [];
            } catch {
              return [];
            }
          }),
      ).size >= 2);
  // A saved seller/listing association already establishes whose company this
  // is. Ordinary company details need proof of that business, not a fresh
  // public personal identifier on every page. Person-specific claims still
  // require their own person match (for example a LinkedIn URL or job title).
  const knownBusinessName = input.original.knownBusiness?.name;
  const knownCompanyDetail =
    Boolean(knownBusinessName) &&
    input.subject === "person" &&
    [
      "company",
      "companyWebsite",
      "companyDomain",
      "companyDescription",
      "industry",
      "businessType",
    ].includes(input.fieldKey) &&
    knownBusinessName === input.original.businessName &&
    check.businessMatch === "confirmed" &&
    quotes.some(
      (q) =>
        q.purpose === "identity" &&
        normalizedResearchText(q.text).includes(
          normalizedResearchText(knownBusinessName ?? ""),
        ),
    );
  if (
    input.subject !== "business" &&
    !knownCompanyDetail &&
    !matchesSourcedPerson({ ...input, verification: check }) &&
    (check.personMatch !== "confirmed" || (!anchored && !contextual))
  ) {
    return {
      decision: "candidate",
      reason: "CONTACT_IDENTIFIER_NOT_CORROBORATED",
    };
  }
  if (
    input.subject === "business" &&
    (check.businessMatch !== "confirmed" || (!anchored && !contextual))
  ) {
    return {
      decision: "candidate",
      reason: "BUSINESS_IDENTIFIER_NOT_CORROBORATED",
    };
  }
  if (
    input.subject === "link" &&
    (check.dealBusinessMatch !== "confirmed" ||
      !["owner", "operator"].includes(check.relationship) ||
      (!reasoned && !quotes.some((q) => q.purpose === "deal")))
  ) {
    return {
      decision: "candidate",
      reason: "SELLER_BUSINESS_RELATIONSHIP_NOT_ESTABLISHED",
    };
  }
  if (
    ["ownerName", "ownerEmail", "ownerPhone"].includes(input.fieldKey) &&
    !["owner", "operator"].includes(check.relationship)
  )
    return { decision: "candidate", reason: "OWNERSHIP_NOT_ESTABLISHED" };
  return { decision: "apply", reason: check.reason };
}

export function researchEvidenceCanPublish(evidence: unknown): boolean {
  if (!evidence || typeof evidence !== "object") return false;
  const record = evidence as Record<string, unknown>;
  return (
    ["identity-v1", RESEARCH_IDENTITY_POLICY_VERSION].includes(
      String(record.policyVersion),
    ) &&
    typeof record.assessmentId === "string" &&
    record.acceptance === "apply" &&
    Array.isArray(record.observationIds) &&
    record.observationIds.length > 0
  );
}

export function researchFactCanPublish(fact: {
  status: string;
  sourceType?: string;
  resolutionKind?: string | null;
  provider?: string;
  evidence?: unknown;
}): boolean {
  return (
    ["ACCEPTED", "AUTO_APPLIED"].includes(fact.status) &&
    (fact.sourceType === "USER" ||
      fact.sourceType === "IMPORT" ||
      fact.resolutionKind === "HUMAN" ||
      fact.provider === "guided-broker-fixture" ||
      researchEvidenceCanPublish(fact.evidence))
  );
}
